import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Search, Filter, BookOpen, FileText, Calendar, Tag, Globe, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ClearableInput from "../components/ClearableInput";
import BlueCubeSpinner from "../components/BlueCubeSpinner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import Navigation from "../components/Navigation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SearchPage = ({ user }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [results, setResults] = useState([]);
  const [dynamicCategories, setDynamicCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [filters, setFilters] = useState({
    year: searchParams.get('year') || '',
    language: searchParams.get('language') || '',
    tags: searchParams.get('tags') || '',
    file_type: searchParams.get('file_type') || '',
    access_level: searchParams.get('access_level') || '',
    category: searchParams.get('category') || '',
    sort_by: searchParams.get('sort_by') || 'created_at',
    sort_order: searchParams.get('sort_order') || 'desc'
  });
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();
  const pendingScrollRef = useRef(null);

  // Restore UI state (scroll position, filters panel visibility) when returning via Back
  useEffect(() => {
    try {
      const raw = typeof window !== 'undefined' ? sessionStorage.getItem('search_ui_state') : null;
      if (raw) {
        const parsed = JSON.parse(raw);
        if (typeof parsed.showFilters === 'boolean') {
          setShowFilters(parsed.showFilters);
        }
        // Defer scroll — will fire once results are rendered
        if (typeof parsed.scrollY === 'number' && parsed.scrollY > 0) {
          pendingScrollRef.current = parsed.scrollY;
        }
      }
    } catch (e) {
      console.error('Failed to restore search UI state', e);
    }
  }, []);

  // Execute deferred scroll restoration after results render
  useEffect(() => {
    if (pendingScrollRef.current != null && !loading && results.length > 0) {
      requestAnimationFrame(() => {
        window.scrollTo({ top: pendingScrollRef.current, behavior: 'auto' });
        pendingScrollRef.current = null;
      });
    }
  }, [loading, results]);

  // Track scroll position continuously
  const scrollYRef = useRef(0);
  useEffect(() => {
    const onScroll = () => { scrollYRef.current = window.scrollY; };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Persist UI state when leaving the page
  useEffect(() => {
    return () => {
      try {
        const state = {
          scrollY: scrollYRef.current,
          showFilters,
        };
        sessionStorage.setItem('search_ui_state', JSON.stringify(state));
      } catch (e) {
        console.error('Failed to save search UI state', e);
      }
    };
  }, [showFilters]);

  useEffect(() => {
    performSearch();
  }, [searchParams]);

  const performSearch = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchParams.get('q')) params.append('q', searchParams.get('q'));
      if (searchParams.get('year')) params.append('year', searchParams.get('year'));
      if (searchParams.get('language')) params.append('language', searchParams.get('language'));
      if (searchParams.get('tags')) params.append('tags', searchParams.get('tags'));
      if (searchParams.get('file_type')) params.append('file_type', searchParams.get('file_type'));
      if (searchParams.get('access_level')) params.append('access_level', searchParams.get('access_level'));
      if (searchParams.get('category') && searchParams.get('category') !== 'all') params.append('category', searchParams.get('category'));
      if (searchParams.get('sort_by')) params.append('sort_by', searchParams.get('sort_by'));
      if (searchParams.get('sort_order')) params.append('sort_order', searchParams.get('sort_order'));
      // Always request a larger page size so you can see (up to) all matching items
      params.append('per_page', '100');

      const response = await axios.get(`${API}/search?${params.toString()}`);
      setResults(response.data.items);
      setTotal(response.data.total);
      const cats = response.data.filters_available?.categories || [];
      const std = ["new", "article", "thesis", "course", "report", "book", "note", "preprint"];
      setDynamicCategories(cats.filter((c) => c && !std.includes(c.toLowerCase())));

      // Mark onboarding "searched" step for logged-in users (fire-and-forget, once)
      const token = localStorage.getItem('access_token');
      if (token && !window.__onboardingSearchMarked) {
        window.__onboardingSearchMarked = true;
        axios.post(`${API}/onboarding/searched`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => {});
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (query.trim()) params.append('q', query);
    Object.entries(filters).forEach(([key, value]) => {
      if (value && !(key === 'category' && value === 'all')) params.append(key, value);
    });
    setSearchParams(params);
  };

  const handleSort = (field) => {
    const newOrder = filters.sort_by === field && filters.sort_order === 'asc' ? 'desc' : 'asc';
    setFilters({...filters, sort_by: field, sort_order: newOrder});
    const params = new URLSearchParams(searchParams);
    params.set('sort_by', field);
    params.set('sort_order', newOrder);
    if (query) params.set('q', query);
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({
      year: '',
      language: '',
      tags: '',
      file_type: '',
      access_level: '',
      category: '',
      sort_by: 'created_at',
      sort_order: 'desc',
    });
    setSearchParams(query ? { q: query } : {});
  };

  return (
    <div className="min-h-screen bg-sky-50">
      <Navigation user={user} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Fraunces, serif' }}>
            Search the Archive
          </h1>
          <button
            type="button"
            className="flex items-center text-xs text-slate-500 hover:text-slate-700 border border-slate-200 rounded-full px-2 py-1"
            onClick={() => alert('Type any word, author, or title in the search box. Use Filters to narrow by year, language, or tags. Click a result to open its details and preview.')}
          >
            <span className="inline-block w-4 h-4 mr-1 rounded-full border border-slate-400 flex items-center justify-center text-[10px]">?</span>
            Help
          </button>
        </div>

        <section className="mb-6 max-w-4xl text-slate-600 text-sm leading-relaxed" data-testid="search-intro">
          <p>
            Browse thousands of <strong>engineering</strong>, <strong>extractive metallurgy</strong>, <strong>mining</strong>, <strong>hydrometallurgy</strong>, and <strong>chemistry</strong> documents in a single searchable library.
            Type any keyword, author name, or paper title to search across titles, abstracts, tags, and the full text of every uploaded PDF.
            Use the filters to narrow by publication year, language, category, or organization — or open <a href="/smart-search" className="text-orange-600 hover:underline">Smart Search</a> for advanced faceted filtering.
          </p>
        </section>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="mb-8">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <ClearableInput
                type="text"
                placeholder="Search..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-12 h-12 bg-white border-slate-200"
                data-testid="search-page-input"
              />
            </div>
            <Button
              type="submit"
              className="h-12 px-6 bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="search-page-submit"
            >
              Search
            </Button>
            <Button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              variant="outline"
              className="h-12 px-6"
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
          </div>
        </form>

        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-white border border-slate-200 p-6 rounded-md mb-8" data-testid="filters-panel">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-lg" style={{ fontFamily: 'Fraunces, serif' }}>Filters</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                data-testid="clear-filters-btn"
              >
                Clear All
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Category</label>
                <Select
                  value={filters.category}
                  onValueChange={(value) => setFilters({ ...filters, category: value })}
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="article">Article</SelectItem>
                    <SelectItem value="thesis">Thesis</SelectItem>
                    <SelectItem value="course">Course</SelectItem>
                    <SelectItem value="report">Report</SelectItem>
                    <SelectItem value="book">Book</SelectItem>
                    <SelectItem value="note">Note</SelectItem>
                    <SelectItem value="preprint">Preprint</SelectItem>
                    {dynamicCategories.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Year</label>
                <ClearableInput
                  type="number"
                  placeholder="e.g., 2024"
                  value={filters.year}
                  onChange={(e) => setFilters({...filters, year: e.target.value})}
                  className="bg-white"
                  data-testid="filter-year"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Language</label>
                <ClearableInput
                  type="text"
                  placeholder="e.g., en"
                  value={filters.language}
                  onChange={(e) => setFilters({...filters, language: e.target.value})}
                  className="bg-white"
                  data-testid="filter-language"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Tags</label>
                <ClearableInput
                  type="text"
                  placeholder="e.g., Research"
                  value={filters.tags}
                  onChange={(e) => setFilters({...filters, tags: e.target.value})}
                  className="bg-white"
                  data-testid="filter-tags"
                />
              </div>
            </div>
          </div>
        )}

        {/* Category quick filters */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="text-xs text-slate-600 mr-2">Browse by category:</span>
          <Link
            to="/categories"
            className="text-xs font-semibold text-orange-700 hover:text-orange-900 underline underline-offset-2 mr-1 order-last"
            data-testid="view-all-categories-link"
          >
            View all categories →
          </Link>
          {[
            { value: "all", label: "Browse all" },
            { value: "new", label: "New" },
            { value: "article", label: "Article" },
            { value: "report", label: "Report" },
            { value: "course", label: "Course" },
            { value: "thesis", label: "Thesis" },
            { value: "book", label: "Book" },
            { value: "note", label: "Note" },
            { value: "preprint", label: "Preprint" },
            ...dynamicCategories.map((c) => ({ value: c, label: c })),
          ].map((cat) => (
            <button
              key={cat.value}
              type="button"
              onClick={() => {
                const nextFilters = {
                  ...filters,
                  category: cat.value === "all" ? "all" : cat.value,
                };
                setFilters(nextFilters);
                const params = new URLSearchParams();
                if (query.trim()) params.append("q", query);
                Object.entries(nextFilters).forEach(([key, value]) => {
                  if (value && !(key === "category" && value === "all")) {
                    params.append(key, value);
                  }
                });
                setSearchParams(params);
              }}
              className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                (filters.category || "all") === cat.value
                  ? "bg-orange-500 text-white border-orange-500"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Results */}
        <div>
          <div className="mb-6 flex items-center justify-between">
            <p className="text-slate-600" data-testid="results-count">
              {loading ? 'Searching...' : `${total} result${total !== 1 ? 's' : ''} found`}
            </p>
            
            {/* Sort Options - Desktop */}
            <div className="hidden md:flex items-center space-x-2">
              <span className="text-sm text-slate-600">Sort by:</span>
              <button
                onClick={() => handleSort('title')}
                className={`px-3 py-1 text-sm rounded ${filters.sort_by === 'title' ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                data-testid="sort-title-btn"
              >
                Title {filters.sort_by === 'title' && (filters.sort_order === 'asc' ? '↑' : '↓')}
              </button>
              <button
                onClick={() => handleSort('authors')}
                className={`px-3 py-1 text-sm rounded ${filters.sort_by === 'authors' ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                data-testid="sort-authors-btn"
              >
                Authors {filters.sort_by === 'authors' && (filters.sort_order === 'asc' ? '↑' : '↓')}
              </button>
              <button
                onClick={() => handleSort('year')}
                className={`px-3 py-1 text-sm rounded ${filters.sort_by === 'year' ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                data-testid="sort-year-btn"
              >
                Year {filters.sort_by === 'year' && (filters.sort_order === 'asc' ? '↑' : '↓')}
              </button>
              <button
                onClick={() => handleSort('created_at')}
                className={`px-3 py-1 text-sm rounded ${filters.sort_by === 'created_at' ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                data-testid="sort-date-btn"
              >
                Date Added {filters.sort_by === 'created_at' && (filters.sort_order === 'asc' ? '↑' : '↓')}
              </button>
            </div>

            {/* Sort Options - Mobile (Dropdown) */}
            <div className="md:hidden flex items-center space-x-3">
              <span className="text-sm text-slate-600">Sort by:</span>
              <Select 
                value={filters.sort_by} 
                onValueChange={(value) => handleSort(value)}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at">Date Added</SelectItem>
                  <SelectItem value="title">Title</SelectItem>
                  <SelectItem value="authors">Authors</SelectItem>
                  <SelectItem value="year">Year</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <BlueCubeSpinner />
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">No results found. Try adjusting your search.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {results.map((item) => (
                <div
                  key={item.id}
                  className="bg-white border border-slate-200 p-6 rounded-md card-hover cursor-pointer"
                  onClick={() => navigate(`/items/${item.id}`)}
                  data-testid={`search-result-${item.id}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Thumbnail */}
                    {item.thumbnail_id && item.file_type === 'PDF' ? (
                      <div className="flex-shrink-0 w-[100px] h-[133px] bg-slate-100 rounded overflow-hidden border border-slate-200">
                        <img 
                          src={`${API}/items/${item.id}/thumbnail`}
                          alt={`Preview of ${item.title}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="w-8 h-8 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg></div>';
                          }}
                        />
                      </div>
                    ) : item.has_file ? (
                      <div className="flex-shrink-0 w-[100px] h-[133px] bg-slate-100 rounded overflow-hidden border border-slate-200 flex items-center justify-center">
                        <FileText className="w-8 h-8 text-slate-300" />
                      </div>
                    ) : null}
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Fraunces, serif' }}>
                          {item.title}
                        </h3>
                        {item.category === 'new' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700 border border-green-200 uppercase tracking-wide">
                            NEW
                          </span>
                        )}
                      </div>
                      {item.authors && item.authors.length > 0 && (
                        <p className="text-sm text-slate-600 mb-2">
                          {item.authors.join(', ')}
                        </p>
                      )}
                      {item.abstract && (
                        <p className="text-slate-600 line-clamp-2 mb-3">
                          {item.abstract}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        {item.year && (
                          <span className="inline-flex items-center px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded">
                            <Calendar className="w-3 h-3 mr-1" />
                            {item.year}
                          </span>
                        )}
                        {item.language && (
                          <span className="inline-flex items-center px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded">
                            <Globe className="w-3 h-3 mr-1" />
                            {item.language}
                          </span>
                        )}
                        {item.file_type && (
                          <span className="inline-flex items-center px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded">
                            <FileText className="w-3 h-3 mr-1" />
                            {item.file_type}
                          </span>
                        )}
                        {item.tags && item.tags.map((tag, idx) => (
                          <span key={idx} className="inline-flex items-center px-2 py-1 bg-orange-50 text-orange-700 text-xs rounded">
                            <Tag className="w-3 h-3 mr-1" />
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchPage;